import { NodeChange, NodePositionChange, XYPosition } from '@xyflow/react';

import { CanvasElementType, CanvasEvent, CanvasEventType } from '../canvas-types';
import { RfNodeStickyNote, RfNodeTextNode, SweepCanvasRfNode } from '../canvas-types/nodeTypesData';
import { ObjectOperations } from './objectOperations';

export interface HandleDimensionChangeResult {
  hasDimensionChange: boolean;
  canvasEvent: CanvasEvent | null;
  appliedNodeChanges: NodeChange<SweepCanvasRfNode>[];
}

const roundXYPosition = (position: XYPosition) => {
  return {
    x: Math.round(position.x),
    y: Math.round(position.y),
  };
};

/**
 * Handles dimension changes for sections and sticky notes.
 * When a section is resized (dimensions change), several change events can be triggered.
 * If it's resized for example from the top left corner, the position change event will be triggered.
 * This is ok. However other children changes will be triggered as well.
 * React-flow doesn't have an option to keep the child elements fixed relative to the origin of the parent.
 * So we need to handle this case separately.
 * We basically only want to process the position change event for the section.
 * And not the children changes. So we ignore all other change events.
 */
export const handleDimensionChange = (
  nodeChanges: NodeChange<SweepCanvasRfNode>[],
  nodeOperations: ObjectOperations<SweepCanvasRfNode>,
  nodesTextScaleRef: React.MutableRefObject<Map<string, number>>,
): HandleDimensionChangeResult => {
  const dimensionChange = nodeChanges.find((nodeChange) => nodeChange.type === 'dimensions');

  if (!dimensionChange) {
    return {
      hasDimensionChange: false,
      canvasEvent: null,
      appliedNodeChanges: [],
    };
  }
  const node = nodeOperations.getObject(dimensionChange.id);

  const relativePositionChange = nodeChanges.find(
    (nodeChange) => nodeChange.type === 'position' && nodeChange.id === dimensionChange.id,
  ) as NodePositionChange | undefined;

  if (!node) {
    return {
      hasDimensionChange: true,
      canvasEvent: null,
      appliedNodeChanges: [],
    };
  }

  // Only allow Groups, StandaloneNodes, and StickyNotes to expand their parent
  const canExpandParent =
    node.type === CanvasElementType.GROUP ||
    node.type === CanvasElementType.STANDALONE_NODE ||
    node.type === CanvasElementType.STICKY_NOTE;

  nodeOperations.setObject(node.id, (node) => ({
    ...node,
    expandParent: canExpandParent && !dimensionChange.resizing,
  }));

  let canvasEvent: CanvasEvent | null = null;
  if (dimensionChange.resizing === false) {
    if (node?.type === CanvasElementType.SECTION) {
      canvasEvent = {
        type: CanvasEventType.ResizeSection,
        id: dimensionChange.id,
        width: Math.round(dimensionChange.dimensions?.width ?? 0),
        height: Math.round(dimensionChange.dimensions?.height ?? 0),
        xyPosition: roundXYPosition(relativePositionChange?.position ?? node.position),
      };
    }
    if (node?.type === CanvasElementType.TEXT_NODE) {
      canvasEvent = {
        type: CanvasEventType.ResizeTextNode,
        id: dimensionChange.id,
        width: Math.round(dimensionChange.dimensions?.width ?? node.width ?? 0),
        height: Math.round(dimensionChange.dimensions?.height ?? node.height ?? 0),
        xyPosition: roundXYPosition(relativePositionChange?.position ?? node.position),
        textScale: nodesTextScaleRef.current.get(dimensionChange.id) ?? node.data.textScale ?? 1,
      };
      nodeOperations.setObject(dimensionChange.id, (node: RfNodeTextNode) => ({
        ...node,
        data: {
          ...node.data,
          textScale: nodesTextScaleRef.current.get(dimensionChange.id) ?? node.data.textScale ?? 1,
        },
      }));
    }
    if (node?.type === CanvasElementType.STICKY_NOTE) {
      canvasEvent = {
        type: CanvasEventType.ResizeStickyNote,
        id: dimensionChange.id,
        width: Math.round(dimensionChange.dimensions?.width ?? node.width ?? 0),
        height: Math.round(dimensionChange.dimensions?.height ?? node.height ?? 0),
        xyPosition: roundXYPosition(relativePositionChange?.position ?? node.position),
        textScale: nodesTextScaleRef.current.get(dimensionChange.id) ?? node.data.textScale ?? 1,
      };
      nodeOperations.setObject(dimensionChange.id, (node: RfNodeStickyNote) => ({
        ...node,
        data: {
          ...node.data,
          textScale: nodesTextScaleRef.current.get(dimensionChange.id) ?? node.data.textScale ?? 1,
        },
      }));
    }
  }

  const appliedNodeChanges = [
    dimensionChange,
    ...(relativePositionChange ? [relativePositionChange] : []),
  ];

  return {
    hasDimensionChange: true,
    canvasEvent,
    appliedNodeChanges,
  };
};
